import React, { useCallback, useEffect, useMemo } from 'react';
import Pusher from 'pusher-js';

import { PusherConfigSelector } from 'store/chats/selectors';
import {
  addLastMessageToChat,
  addNotification as updateNotificationCounter,
  addTechTyping,
} from 'store/chats';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { addNotification } from 'store/notifications';
import { createTechnicianConfirmation, createVerificationRequest } from 'store/verificationRequest';

import { decodeAccessToken } from 'utils/getDecodedAccessToken';

import { PusherEvents } from 'constants/events';
import {
  createNotificationFromAdminMessage,
  INewAdminMessage,
  ITechnicianConfirmationRequest,
  IVerificationRequest,
} from 'constants/pusher';
import { IChatMessage, ITechTyping } from 'services/chats/types';

import { LOCAL_STORAGE_TOKEN } from 'constants/storage';

import { CHAT } from 'router/routes';

interface IProps {
  children: React.ReactNode;
}

const PusherProvider: React.FC<IProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const accessToken = localStorage.getItem(LOCAL_STORAGE_TOKEN);
  const pusherConfig = useAppSelector(PusherConfigSelector);

  const handlePusherConnection = useCallback(() => {
    let pusher;
    if (pusherConfig.appId) {
      pusher = new Pusher(pusherConfig.key, {
        cluster: pusherConfig.cluster,
      });
    }
    return pusher;
  }, [pusherConfig.appId, pusherConfig.key, pusherConfig.cluster]);

  const pusher = useMemo(() => {
    return handlePusherConnection();
  }, [handlePusherConnection]);

  const onTechValidationConfirmed = (data: ITechnicianConfirmationRequest): void => {
    dispatch(createTechnicianConfirmation(data));
  };

  const onValidationRequest = (data: IVerificationRequest): void => {
    dispatch(createVerificationRequest(data));
  };

  const onTechTyping = (data: ITechTyping): void => {
    dispatch(addTechTyping(data));
  };

  const onNewAdminMessage = useCallback((data: INewAdminMessage): void => {
    const message: IChatMessage = {
      metadata: data.metadata,
      dateTime: data.dateTime,
      resourceName: data.userName,
      contactName: '',
      message: data.message,
      type: data.type,
      id: new Date().getTime(),
    };

    if (CHAT.replace(':chatId', `${data.chatId}`) === window.location.pathname) {
      dispatch(addLastMessageToChat({ message, chatId: data.chatId }));
    } else {
      // ADD Notification to Notifications
      dispatch(addLastMessageToChat({ message, chatId: data.chatId }));
      dispatch(addNotification(createNotificationFromAdminMessage(data)));
      dispatch(updateNotificationCounter(data.chatId));
    }
  }, []);

  useEffect(() => {
    if (pusher && accessToken) {
      const { configId, contactId } = decodeAccessToken(accessToken);
      const channel = pusher.subscribe(`portal-${configId}-${contactId}`);

      channel.bind(PusherEvents.ValidationRequested, onValidationRequest);
      channel.bind(PusherEvents.NewAdminMessage, onNewAdminMessage);
      channel.bind(PusherEvents.TechValidationConfirmed, onTechValidationConfirmed);
      channel.bind(PusherEvents.TechTyping, onTechTyping);
    }
  }, [handlePusherConnection]);

  useEffect(() => {
    return () => {
      if (pusher) {
        pusher.allChannels().forEach(ch => {
          pusher.unsubscribe(ch.name);
        });
        pusher.disconnect();
      }
    };
  }, []);

  return <>{children}</>;
};

export default PusherProvider;
